import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowLeft,
  Save,
  Upload,
  Star,
  Eye,
  EyeOff,
  Trash2,
  Plus,
  Minus,
  QrCode,
  GripVertical,
} from "lucide-react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { id: "content", label: "Περιεχόμενο" },
  { id: "media", label: "Φωτογραφίες" },
  { id: "visibility", label: "Εμφάνιση" },
  { id: "links", label: "Σύνδεσμοι" },
];

const CATEGORIES = ["Hero", "Χώρος", "Τραπέζι", "Παίκτες", "Dungeon Masters", "Miniatures and Terrain", "Διακόσμηση", "Events", "QR"];

export default function AdminHyenaDungeon() {
  const { user } = useAuth();
  const [tab, setTab] = useState("content");
  const [content, setContent] = useState(null);
  const [media, setMedia] = useState([]);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const loadContent = async () => {
    const r = await api.get("/hyena-dungeon/content");
    setContent(r.data);
  };
  const loadMedia = async () => {
    const r = await api.get("/hyena-dungeon/media?include_hidden=true");
    setMedia(r.data);
  };

  useEffect(() => {
    loadContent();
    loadMedia();
  }, []);

  const saveContent = async () => {
    setErr("");
    setMsg("");
    try {
      const payload = { ...content };
      delete payload.updated_at;
      delete payload.updated_by;
      await api.put("/hyena-dungeon/content", payload);
      setMsg("Αποθηκεύτηκε ✓");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!content) return <div className="min-h-screen bg-black text-zinc-500 flex items-center justify-center font-mono text-xs">LOADING...</div>;

  return (
    <div data-testid="admin-hyena-page" className="min-h-screen bg-black text-white">
      <div className="border-b border-red-950/60 bg-black/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 text-white hover:text-red-400">
            <ArrowLeft className="w-4 h-4" /> <span className="font-mono text-xs uppercase tracking-widest">Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/hyena-dungeon" data-testid="admin-hyena-view-page" target="_blank" className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-red-400">View Page ↗</Link>
            <span className="text-xs font-mono text-red-400 uppercase tracking-widest">{user?.name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="text-4xl md:text-5xl uppercase font-black" style={{ fontFamily: "'Cinzel', serif" }}>Hyena <span className="text-red-500">Dungeon</span> Admin</h1>

        <div className="flex flex-wrap gap-1 border-b border-red-950/50 mt-8 mb-8">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-hyena-tab-${t.id}`} className={`px-5 py-3 text-xs font-mono uppercase tracking-widest border-b-2 ${tab === t.id ? "border-red-500 text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>{t.label}</button>
          ))}
        </div>

        {err && <div data-testid="admin-hyena-error" className="text-red-400 text-xs font-mono mb-4">{err}</div>}
        {msg && <div data-testid="admin-hyena-success" className="text-emerald-400 text-xs font-mono mb-4">{msg}</div>}

        {tab === "content" && <ContentTab content={content} setContent={setContent} save={saveContent} />}
        {tab === "media" && <MediaTab media={media} reload={loadMedia} />}
        {tab === "visibility" && <VisibilityTab media={media} reload={loadMedia} />}
        {tab === "links" && <LinksTab content={content} setContent={setContent} save={saveContent} />}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, textarea, rows, testid, maxLength }) {
  const T = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">{label}</span>
      <T
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        maxLength={maxLength}
        data-testid={testid}
        className={`mt-2 w-full bg-black border border-zinc-800 text-white px-4 py-3 focus:outline-none focus:border-red-500 transition-colors ${textarea ? "font-mono text-sm" : ""}`}
      />
    </label>
  );
}

function ContentTab({ content, setContent, save }) {
  const set = (k, v) => setContent({ ...content, [k]: v });
  const setFeature = (i, k, v) => {
    const fc = [...content.feature_cards];
    fc[i] = { ...fc[i], [k]: v };
    set("feature_cards", fc);
  };
  const addFeature = () => set("feature_cards", [...content.feature_cards, { title: "", description: "" }]);
  const rmFeature = (i) => set("feature_cards", content.feature_cards.filter((_, x) => x !== i));

  const setExp = (i, v) => {
    const items = [...content.experience_items];
    items[i] = v;
    set("experience_items", items);
  };
  const addExp = () => set("experience_items", [...content.experience_items, ""]);
  const rmExp = (i) => set("experience_items", content.experience_items.filter((_, x) => x !== i));

  return (
    <div className="space-y-8">
      <div className="grid gap-4">
        <Field label="Title" value={content.page_title} onChange={(v) => set("page_title", v)} testid="hyena-content-title" maxLength={100} />
        <Field label="Subtitle" value={content.subtitle} onChange={(v) => set("subtitle", v)} testid="hyena-content-subtitle" maxLength={200} />
        <Field label="Supporting text" value={content.supporting_text} onChange={(v) => set("supporting_text", v)} textarea rows={3} testid="hyena-content-supporting" maxLength={1000} />
        <Field label="Full description (paragraphs separated by empty line)" value={content.description} onChange={(v) => set("description", v)} textarea rows={10} testid="hyena-content-description" maxLength={10000} />
      </div>

      <section>
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Feature Cards</span>
          <button onClick={addFeature} data-testid="hyena-add-feature" className="text-red-400 hover:text-red-300 text-xs uppercase tracking-widest font-mono flex items-center gap-1"><Plus className="w-3 h-3" />Add card</button>
        </div>
        <div className="space-y-3">
          {content.feature_cards.map((c, i) => (
            <div key={i} className="border border-zinc-800 p-4 space-y-2">
              <input value={c.title} onChange={(e) => setFeature(i, "title", e.target.value)} data-testid={`feature-title-${i}`} placeholder="Title" className="w-full bg-black border border-zinc-800 text-white px-3 py-2 focus:border-red-500 focus:outline-none text-sm" />
              <textarea value={c.description} onChange={(e) => setFeature(i, "description", e.target.value)} data-testid={`feature-desc-${i}`} placeholder="Description" rows={2} className="w-full bg-black border border-zinc-800 text-white px-3 py-2 focus:border-red-500 focus:outline-none text-sm font-mono" />
              <button onClick={() => rmFeature(i)} className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1"><Minus className="w-3 h-3" />Remove</button>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Experience items</span>
          <button onClick={addExp} data-testid="hyena-add-exp" className="text-red-400 hover:text-red-300 text-xs uppercase tracking-widest font-mono flex items-center gap-1"><Plus className="w-3 h-3" />Add item</button>
        </div>
        <div className="space-y-2">
          {content.experience_items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <input value={it} onChange={(e) => setExp(i, e.target.value)} data-testid={`exp-item-${i}`} className="flex-1 bg-black border border-zinc-800 text-white px-3 py-2 focus:border-red-500 focus:outline-none text-sm" />
              <button onClick={() => rmExp(i)} className="border border-red-900 text-red-400 hover:border-red-500 px-3"><Minus className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="mb-3 font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">CTA</div>
        <div className="grid gap-4">
          <Field label="CTA Title" value={content.cta_title} onChange={(v) => set("cta_title", v)} testid="cta-title" maxLength={200} />
          <Field label="CTA Text" value={content.cta_text} onChange={(v) => set("cta_text", v)} textarea rows={2} testid="cta-text" maxLength={1000} />
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Primary label" value={content.cta_primary_label} onChange={(v) => set("cta_primary_label", v)} testid="cta-primary-label" maxLength={60} />
            <Field label="Social label" value={content.cta_social_label} onChange={(v) => set("cta_social_label", v)} testid="cta-social-label" maxLength={60} />
            <Field label="Contact label" value={content.cta_contact_label} onChange={(v) => set("cta_contact_label", v)} testid="cta-contact-label" maxLength={60} />
          </div>
        </div>
      </section>

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} data-testid="admin-hyena-save-content" className="bg-red-600 text-white font-bold uppercase tracking-widest px-6 py-3 text-xs hover:bg-red-500 inline-flex items-center gap-2 shadow-lg">
          <Save className="w-3.5 h-3.5" /> Αποθήκευση
        </button>
      </div>
    </div>
  );
}

function LinksTab({ content, setContent, save }) {
  const set = (k, v) => setContent({ ...content, [k]: v });
  return (
    <div className="space-y-4">
      <Field label="Participation URL (κουμπί 'Δήλωσε συμμετοχή' — άδειο = κρύβεται)" value={content.participation_url} onChange={(v) => set("participation_url", v)} testid="links-participation" maxLength={500} />
      <Field label="Social URL (κουμπί 'Ακολούθησέ μας' — π.χ. Linktree)" value={content.social_url} onChange={(v) => set("social_url", v)} testid="links-social" maxLength={500} />
      <Field label="Contact URL (κουμπί 'Επικοινώνησε μαζί μας')" value={content.contact_url} onChange={(v) => set("contact_url", v)} testid="links-contact" maxLength={500} />
      <Field label="SEO Title" value={content.seo_title} onChange={(v) => set("seo_title", v)} testid="links-seo-title" maxLength={200} />
      <Field label="SEO Description" value={content.seo_description} onChange={(v) => set("seo_description", v)} textarea rows={2} testid="links-seo-description" maxLength={400} />
      <div className="flex justify-end">
        <button onClick={save} data-testid="admin-hyena-save-links" className="bg-red-600 text-white font-bold uppercase tracking-widest px-6 py-3 text-xs hover:bg-red-500 inline-flex items-center gap-2">
          <Save className="w-3.5 h-3.5" /> Αποθήκευση
        </button>
      </div>
    </div>
  );
}

function MediaTab({ media, reload }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("Χώρος");
  const [asHero, setAsHero] = useState(false);
  const [asQr, setAsQr] = useState(false);
  const [altText, setAltText] = useState("");
  const [caption, setCaption] = useState("");
  const [uploadErr, setUploadErr] = useState("");

  const onUpload = async (files) => {
    setUploading(true);
    setUploadErr("");
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", asQr ? "QR" : category);
        fd.append("alt_text", altText);
        fd.append("caption", caption);
        fd.append("is_hero", String(asHero && files.length === 1));
        fd.append("is_qr", String(asQr));
        await api.post("/hyena-dungeon/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setAltText("");
      setCaption("");
      setAsHero(false);
      setAsQr(false);
      if (fileRef.current) fileRef.current.value = "";
      await reload();
    } catch (e) {
      setUploadErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const patch = async (id, body) => {
    await api.patch(`/hyena-dungeon/media/${id}`, body);
    await reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete image?")) return;
    await api.delete(`/hyena-dungeon/media/${id}`);
    await reload();
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const [items, setItems] = useState(media);
  useEffect(() => setItems(media), [media]);

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((m) => m.id === active.id);
    const newIndex = items.findIndex((m) => m.id === over.id);
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);
    await api.post("/hyena-dungeon/media/reorder", { items: next.map((m, i) => ({ id: m.id, sort_order: i })) });
  };

  return (
    <div className="space-y-8">
      {/* UPLOADER */}
      <div className="border border-red-950/60 bg-black p-5" data-testid="hyena-uploader">
        <div className="font-mono text-[10px] tracking-[0.3em] text-red-400 uppercase mb-4">Upload new images (max 10MB, JPG/PNG/WebP)</div>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <label className="block">
            <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="upload-category" className="mt-2 w-full bg-black border border-zinc-800 text-white px-3 py-2 focus:outline-none focus:border-red-500 text-sm">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <div className="flex items-end gap-4">
            <label className="flex items-center gap-2 text-xs font-mono text-zinc-400 uppercase tracking-widest cursor-pointer">
              <input type="checkbox" checked={asHero} onChange={(e) => setAsHero(e.target.checked)} data-testid="upload-hero" /> Set as HERO
            </label>
            <label className="flex items-center gap-2 text-xs font-mono text-zinc-400 uppercase tracking-widest cursor-pointer">
              <input type="checkbox" checked={asQr} onChange={(e) => setAsQr(e.target.checked)} data-testid="upload-qr" /> QR code
            </label>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <input value={altText} onChange={(e) => setAltText(e.target.value)} data-testid="upload-alt" placeholder="Alt text (Greek, for accessibility)" className="bg-black border border-zinc-800 text-white px-3 py-2 focus:outline-none focus:border-red-500 text-sm" />
          <input value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="upload-caption" placeholder="Caption (optional)" className="bg-black border border-zinc-800 text-white px-3 py-2 focus:outline-none focus:border-red-500 text-sm" />
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(e) => e.target.files.length && onUpload(Array.from(e.target.files))} data-testid="upload-input" className="text-xs text-zinc-400" />
        {uploading && <div className="mt-2 text-xs text-red-400 font-mono">Uploading…</div>}
        {uploadErr && <div data-testid="upload-error" className="mt-2 text-xs text-red-400 font-mono">{uploadErr}</div>}
      </div>

      {/* GRID with drag-drop */}
      <div>
        <div className="mb-3 font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Photos ({items.length}) · drag to reorder</div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((m) => m.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="media-grid">
              {items.map((m) => <SortableItem key={m.id} m={m} patch={patch} del={del} />)}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

function SortableItem({ m, patch, del }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: m.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`media-item-${m.id}`} className="border border-zinc-800 bg-black overflow-hidden">
      <div className="relative aspect-square bg-zinc-950">
        <img src={`${API_BASE}/hyena-dungeon/media/${m.id}/file?variant=thumb`} alt={m.alt_text || ""} className="w-full h-full object-contain" />
        <button {...attributes} {...listeners} className="absolute top-2 left-2 bg-black/70 border border-zinc-700 p-1.5 cursor-grab active:cursor-grabbing"><GripVertical className="w-3 h-3 text-zinc-300" /></button>
        <div className="absolute top-2 right-2 flex gap-1">
          {m.is_hero && <span className="bg-red-600 text-black text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5">Hero</span>}
          {m.is_qr && <span className="bg-purple-600 text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5">QR</span>}
          {!m.is_visible && <span className="bg-zinc-800 text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5">Hidden</span>}
        </div>
      </div>
      <div className="p-3 space-y-2 text-xs">
        <input value={m.alt_text || ""} onChange={(e) => patch(m.id, { alt_text: e.target.value })} data-testid={`alt-${m.id}`} placeholder="alt text" className="w-full bg-black border border-zinc-800 text-white px-2 py-1.5 focus:border-red-500 focus:outline-none text-xs" />
        <input value={m.caption || ""} onChange={(e) => patch(m.id, { caption: e.target.value })} placeholder="caption" className="w-full bg-black border border-zinc-800 text-white px-2 py-1.5 focus:border-red-500 focus:outline-none text-xs" />
        <select value={m.category} onChange={(e) => patch(m.id, { category: e.target.value })} className="w-full bg-black border border-zinc-800 text-white px-2 py-1.5 focus:border-red-500 focus:outline-none text-xs">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => patch(m.id, { is_hero: !m.is_hero })} data-testid={`hero-btn-${m.id}`} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${m.is_hero ? "border-red-500 text-red-400" : "border-zinc-800 text-zinc-400 hover:border-red-500"}`}><Star className="w-3 h-3 inline mr-1" />{m.is_hero ? "Hero" : "Set Hero"}</button>
          <button onClick={() => patch(m.id, { is_qr: !m.is_qr })} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${m.is_qr ? "border-purple-500 text-purple-300" : "border-zinc-800 text-zinc-400 hover:border-purple-500"}`}><QrCode className="w-3 h-3 inline mr-1" />QR</button>
          <button onClick={() => patch(m.id, { is_visible: !m.is_visible })} data-testid={`vis-btn-${m.id}`} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-zinc-800 text-zinc-400 hover:border-white">{m.is_visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}</button>
          <button onClick={() => del(m.id)} data-testid={`del-btn-${m.id}`} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-red-900 text-red-400 hover:border-red-500 ml-auto"><Trash2 className="w-3 h-3" /></button>
        </div>
      </div>
    </div>
  );
}

function VisibilityTab({ media, reload }) {
  const patch = async (id, body) => {
    await api.patch(`/hyena-dungeon/media/${id}`, body);
    await reload();
  };
  return (
    <div>
      <div className="mb-4 font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Έλεγχος εμφάνισης - επίλεξε ποιες φωτογραφίες θα εμφανίζονται στη σελίδα</div>
      <div className="border border-zinc-900">
        {media.map((m) => (
          <div key={m.id} data-testid={`vis-row-${m.id}`} className="flex items-center gap-3 border-b border-zinc-900 p-3 last:border-b-0">
            <img src={`${API_BASE}/hyena-dungeon/media/${m.id}/file?variant=thumb`} alt="" className="w-16 h-16 object-cover bg-zinc-950" />
            <div className="flex-1 text-sm">
              <div className="font-medium">{m.title || m.alt_text || m.original_filename || "Untitled"}</div>
              <div className="text-xs text-zinc-500 font-mono">{m.category} · {(m.file_size / 1024).toFixed(0)}KB · {m.width}×{m.height}</div>
            </div>
            {m.is_hero && <span className="bg-red-600 text-black text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5">Hero</span>}
            {m.is_qr && <span className="bg-purple-600 text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5">QR</span>}
            <button onClick={() => patch(m.id, { is_visible: !m.is_visible })} className={`text-[10px] font-mono uppercase tracking-widest px-3 py-2 border ${m.is_visible ? "border-emerald-800 text-emerald-400" : "border-zinc-800 text-zinc-500"}`}>{m.is_visible ? <><Eye className="w-3 h-3 inline mr-1" />Visible</> : <><EyeOff className="w-3 h-3 inline mr-1" />Hidden</>}</button>
          </div>
        ))}
        {media.length === 0 && <div className="p-10 text-center text-zinc-500 text-sm">No images uploaded yet.</div>}
      </div>
    </div>
  );
}
